import React from 'react';
import {
  Brain,
  Lightbulb,
  Loader2,
  Sparkles,
  Video,
  Zap,
} from 'lucide-react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';

const PromptsTab = ({
  generating,
  prompts,
  selectedRecording,
  setSelectedRecording,
  recordings,
  promptHistory,
  loading,
  analyzingPrompts,
  promptInsights,
  handleGenerate,
  handlePublishAll,
  analyzePrompts,
  getFocusAreaLabel,
  openRecordingsReview,
}) => {
  const displayRecordings = recordings;
  const displayHistory = promptHistory;

  const focusColors = {
    marriage: { bg: 'bg-[#E8836B]/10', text: 'text-orange-700 dark:text-[#E8836B]', border: 'border-[#E8836B]/30' },
    parenting: { bg: 'bg-aqua/10', text: 'text-aqua', border: 'border-aqua/30' },
    career: { bg: 'bg-blue-500/10', text: 'text-blue-500', border: 'border-blue-500/30' },
    general: { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/30' },
  };

  return (
    <div className="space-y-6">
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-aqua animate-spin" />
        </div>
      ) : (
        <>
          <Card className="bg-[#1e1e24] border-[#2a2a32] p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-aqua" />
              Prompt Assistant
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              Generate or inspect AI prompt options here. Final review and publishing now happen in the Recordings tab.
            </p>
            {displayRecordings.length === 0 ? (
              <div className="text-center py-6 border-2 border-dashed border-[#2a2a32] rounded-xl">
                <Video className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No recordings available. Upload a recording first to generate prompts.</p>
              </div>
            ) : (
              <div className="flex gap-3 flex-wrap">
                <select
                  value={selectedRecording}
                  onChange={(e) => setSelectedRecording(e.target.value)}
                  className="flex-1 min-w-[250px] bg-[#16161a] border border-[#2a2a32] text-white rounded-lg px-4 py-2"
                >
                  <option value="">Select a Recording...</option>
                  {displayRecordings.map(rec => (
                    <option key={rec.recording_id} value={rec.recording_id}>
                      {rec.title} ({new Date(rec.date).toLocaleDateString()})
                    </option>
                  ))}
                </select>
                <Button
                  onClick={handleGenerate}
                  disabled={generating || !selectedRecording}
                  className="bg-aqua hover:bg-aqua-dark text-charcoal gap-2"
                >
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {generating ? 'Generating...' : 'Generate 3 Prompts'}
                </Button>
              </div>
            )}

            {prompts.length > 0 && (
              <div className="mt-6 space-y-3">
                <h4 className="font-medium text-white">Generated Prompt Options:</h4>
                <p className="text-gray-400 text-sm mb-3">Use these as starting points, then switch to Recordings to choose the one shared weekly focus you want to publish.</p>
                {prompts.map((prompt, idx) => (
                  <div
                    key={prompt.id}
                    className="p-4 bg-[#16161a] rounded-xl border border-[#2a2a32]"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="bg-aqua/10 text-aqua border-aqua/30">Option {idx + 1}</Badge>
                    </div>
                    <p className="text-white">{prompt.text}</p>
                  </div>
                ))}
                <Button
                  onClick={openRecordingsReview}
                  className="w-full mt-4 bg-aqua hover:bg-aqua-dark text-charcoal"
                >
                  <Video className="w-4 h-4 mr-2" />
                  Open Recordings Review To Publish
                </Button>
              </div>
            )}
          </Card>

          <Card className="bg-[#1e1e24] border-[#2a2a32] p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Prompt History</h3>
            </div>

            <div className="space-y-4">
              {displayHistory.length === 0 ? (
                <div className="text-center py-8">
                  <Sparkles className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">No prompts published yet</p>
                  <p className="text-gray-500 text-sm mt-1">Generate and publish prompts to see history here</p>
                </div>
              ) : (
                displayHistory.map((week, idx) => (
                  <div key={idx} className="p-4 bg-[#16161a] rounded-xl">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="text-white font-medium">{week.week}</span>
                        <span className="text-gray-500 text-sm">{week.date}</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {week.prompts.map((prompt, pIdx) => {
                        const colors = focusColors[prompt.focus_area] || focusColors.marriage;
                        return (
                          <div
                            key={pIdx}
                            className="p-3 rounded-lg flex items-center justify-between bg-aqua/10 border border-aqua/30"
                          >
                            <div className="flex-1">
                              <p className="text-white text-sm">{prompt.content}</p>
                              <Badge className={`${colors.bg} ${colors.text} ${colors.border} mt-2`}>
                                {getFocusAreaLabel(prompt.focus_area || 'general')}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              <Badge className="bg-green-500/10 text-green-400">Published</Badge>
                              <span className="text-aqua text-sm">{prompt.engagement || 0} interactions</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>

          <Card className="bg-[#1e1e24] border-[#2a2a32] p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Brain className="w-5 h-5 text-aqua" />
                AI Prompt Analysis
              </h3>
              <Button
                onClick={analyzePrompts}
                disabled={analyzingPrompts}
                className="bg-aqua hover:bg-aqua-dark text-charcoal gap-2"
              >
                {analyzingPrompts ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                Analyze Prompts
              </Button>
            </div>

            {promptInsights ? (
              <div className="space-y-4">
                <div className="p-4 bg-aqua/10 border border-aqua/20 rounded-xl">
                  <p className="text-aqua font-medium">Key Finding:</p>
                  <p className="text-white">{promptInsights.top_performing || promptInsights.topPerforming}</p>
                </div>

                {(promptInsights.suggestions || []).length > 0 && (
                  <div>
                    <p className="text-white font-medium mb-2">Content Ideas Generated:</p>
                    <ul className="space-y-2">
                      {(promptInsights.suggestions || []).map((s, i) => (
                        <li key={i} className="flex items-start gap-2 text-gray-400 text-sm">
                          <Lightbulb className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {(promptInsights.patterns || []).length > 0 && (
                  <div>
                    <p className="text-white font-medium mb-2">Writing Patterns:</p>
                    <div className="flex flex-wrap gap-2">
                      {(promptInsights.patterns || []).map((p, i) => (
                        <Badge key={i} className="bg-[#16161a] text-gray-300">{p}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">Run analysis to discover insights from your prompts</p>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default PromptsTab;
